/**
 * Route guard for internal/staff-only surfaces (e.g. /design-lab).
 *
 * Two gates, both must be true:
 *   1. The build was produced with REACT_APP_ENABLE_INTERNAL_TOOLS=true.
 *      Production builds default to "false" so internal pages do not ship
 *      in customer bundles even if someone forgets to remove a route.
 *   2. The user is authenticated. (Internal tooling that depends on the
 *      logged-in user's context is the only legitimate use case here, and
 *      gating on auth gives us an audit trail.)
 *
 * If the gate fails, render a 404-style "Not Found" page so the URL does
 * not advertise that an internal route exists at this path.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";

function isInternalEnabled(): boolean {
  // Read at call time so the production bundle eliminates the page contents
  // via dead-code elimination once the env var is baked in at build time,
  // while tests can flip the env var per case.
  return process.env.REACT_APP_ENABLE_INTERNAL_TOOLS === "true";
}

function NotFound() {
  const { t } = useTranslation("common");
  return (
    <Box sx={{ p: 6, textAlign: "center" }}>
      <Typography variant="h4" gutterBottom>
        {t("InternalRoute.pageNotFound")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("InternalRoute.pageNotFoundDetail")}
      </Typography>
      <Button variant="contained" href="/">
        {t("InternalRoute.backToHome")}
      </Button>
    </Box>
  );
}

export default function InternalRoute({
  children,
}: {
  children: React.ReactElement;
}) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (!isInternalEnabled()) {
    return <NotFound />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate
        to="/login"
        // redirectedFromProtected is read by PageEventObserver to mark the
        // resulting /login page_view as a dead end (M-03). `from` is left
        // untouched: LoginPage still reads it to bounce the user back.
        state={{ from: location.pathname, redirectedFromProtected: true }}
        replace
      />;
  }

  return children;
}
